"use server";

import { prisma } from "@/lib/db";
import { getGuest } from "@/lib/auth";
import { quoteInput } from "@/lib/validators";
import { assertAvailable, createBooking, loadStayView, markDepositPaid, markRemainingPaid } from "@/lib/bookings";
import { nightsBetween, todayNY, isCheckInDayOrAfter, isStayExpired } from "@/lib/dates";
import { quoteStay } from "@/lib/money";
import { env, stripeEnabled } from "@/lib/env";
import { sendMessage } from "@/lib/mail";
import { notifyUser } from "@/lib/push";
import { staySession } from "@/lib/session";
import { clientIp } from "@/lib/ip";
import { audit } from "@/lib/audit";
import { rateLimit } from "@/lib/rate-limit";
import Stripe from "stripe";

export async function getQuote(input: unknown) {
  const parsed = quoteInput.safeParse(input);
  if (!parsed.success) return { ok: false as const, error: "Check your dates and guests." };
  const listing = await prisma.listing.findFirst({ where: { slug: parsed.data.slug, published: true } });
  if (!listing) return { ok: false as const, error: "Stay not found." };
  if (parsed.data.guests > listing.maxGuests) return { ok: false as const, error: "That exceeds the guest limit." };
  const nights = nightsBetween(parsed.data.checkIn, parsed.data.checkOut);
  if (nights < 1) return { ok: false as const, error: "At least one night is required." };
  try {
    await assertAvailable(listing.id, parsed.data.checkIn, parsed.data.checkOut);
  } catch (e) {
    return { ok: false as const, error: (e as Error).message };
  }
  let percentOff: number | undefined;
  if (parsed.data.promoCode) {
    const promo = await prisma.promoCode.findUnique({ where: { code: parsed.data.promoCode } });
    if (!promo?.active) return { ok: false as const, error: "That promo code isn’t valid." };
    percentOff = promo.percentOff;
  }
  return {
    ok: true as const,
    quote: quoteStay({
      nightlyPrice: listing.nightlyPrice,
      cleaningFee: listing.cleaningFee,
      nights,
      depositBps: listing.depositBps,
      percentOff,
      promoCode: parsed.data.promoCode,
    }),
  };
}

export async function startBooking(input: unknown) {
  const guest = await getGuest();
  if (!guest) return { ok: false as const, error: "Sign in to continue.", login: true };
  if (!guest.email) {
    return { ok: false as const, error: "Add an email so we can send your booking code.", needsEmail: true };
  }
  const parsed = quoteInput.safeParse(input);
  if (!parsed.success) return { ok: false as const, error: "Check your dates and guests." };
  const listing = await prisma.listing.findFirst({ where: { slug: parsed.data.slug, published: true } });
  if (!listing) return { ok: false as const, error: "Stay not found." };

  const ip = await clientIp();
  const limited = await rateLimit(`book:${guest.id}`, 8, 60 * 60 * 1000);
  if (!limited.ok) return { ok: false as const, error: "Too many booking attempts." };

  try {
    const { booking } = await createBooking({
      listingId: listing.id,
      guestId: guest.id,
      guestEmail: guest.email,
      guestName: guest.name ?? guest.email,
      checkIn: parsed.data.checkIn,
      checkOut: parsed.data.checkOut,
      guests: parsed.data.guests,
      promoCode: parsed.data.promoCode,
    });
    await audit("booking.create", { bookingId: booking.id }, guest.id, ip);

    if (stripeEnabled()) {
      const stripe = new Stripe(env().stripeSecret);
      const session = await stripe.checkout.sessions.create({
        mode: "payment",
        customer_email: guest.email,
        line_items: [
          {
            quantity: 1,
            price_data: {
              currency: "usd",
              unit_amount: booking.depositAmount,
              product_data: {
                name: `${listing.title} deposit`,
                description: `${booking.checkIn} – ${booking.checkOut}`,
              },
            },
          },
        ],
        metadata: { bookingId: booking.id, kind: "deposit" },
        success_url: `${env().appUrl}/book/success?booking=${booking.id}`,
        cancel_url: `${env().appUrl}/stay/${listing.slug}`,
      });
      await prisma.booking.update({
        where: { id: booking.id },
        data: { stripeDepositId: session.id },
      });
      return { ok: true as const, checkoutUrl: session.url };
    }

    await finalizeDeposit(booking.id);
    return { ok: true as const, bookingId: booking.id, demo: true };
  } catch (e) {
    return { ok: false as const, error: (e as Error).message };
  }
}

export async function payRemaining() {
  const stay = await staySession();
  if (!stay.bookingId) return { ok: false as const, error: "A booking code is required." };
  const booking = await prisma.booking.findUnique({
    where: { id: stay.bookingId },
    include: { listing: true, guest: true },
  });
  if (!booking?.depositPaidAt) return { ok: false as const, error: "This booking isn’t confirmed." };
  const ip = await clientIp();
  const guest = await getGuest();

  async function stayWithDoor() {
    return loadStayView(booking!.id, booking!.guestId ?? guest?.id, ip);
  }

  if (booking.remainingPaidAt) {
    const view = await stayWithDoor();
    if (view?.doorCode) return { ok: true as const, stay: view, already: true };
    return { ok: false as const, error: "The balance is already paid." };
  }
  if (isStayExpired(booking.checkOut)) return { ok: false as const, error: "This stay has ended." };
  if (!isCheckInDayOrAfter(booking.checkIn)) {
    return { ok: false as const, error: "The balance and door code unlock on check-in day." };
  }

  if (stripeEnabled()) {
    const stripe = new Stripe(env().stripeSecret);
    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      customer_email: booking.guestEmail,
      line_items: [
        {
          quantity: 1,
          price_data: {
            currency: "usd",
            unit_amount: booking.remainingAmount,
            product_data: { name: `${booking.listing.title} balance` },
          },
        },
      ],
      metadata: { bookingId: booking.id, kind: "remaining" },
      success_url: `${env().appUrl}/my-stay?paid=1`,
      cancel_url: `${env().appUrl}/my-stay`,
    });
    await prisma.booking.update({
      where: { id: booking.id },
      data: { stripeRemainingId: session.id },
    });
    return { ok: true as const, checkoutUrl: session.url };
  }

  await markRemainingPaid(booking.id);
  await sendMessage({
    channel: "email",
    to: booking.guestEmail,
    subject: "Balance paid · door code is in the app",
    body: `${booking.listing.title} balance is confirmed. Open My stay to see the door code. We never email the door code.`,
    userId: booking.guestId ?? guest?.id,
    href: "/my-stay",
  });
  if (booking.guestId) {
    await notifyUser(booking.guestId, {
      title: "Balance paid",
      body: "Your door code is ready in My stay.",
      url: "/my-stay",
    });
  }
  const view = await stayWithDoor();
  return { ok: true as const, demo: true, stay: view };
}

export async function finalizeDeposit(bookingId: string, presetCode?: string) {
  const { generateBookingCode, hashWithPepper, normalizeBookingCode } = await import("@/lib/crypto");
  const code = presetCode ?? generateBookingCode();
  await prisma.booking.update({
    where: { id: bookingId },
    data: {
      codeHash: hashWithPepper(normalizeBookingCode(code), env().bookingCodePepper),
      codeHint: code.slice(-2),
    },
  });
  const booking = await markDepositPaid(bookingId);
  await sendMessage({
    channel: "email",
    to: booking.guestEmail,
    subject: `Booking confirmed · code ${code}`,
    body: [
      `${booking.guestName}, your stay at ${booking.listing.title} is confirmed.`,
      ``,
      `Dates: check-in ${booking.checkIn} → check-out ${booking.checkOut} (${booking.nights} night${booking.nights === 1 ? "" : "s"})`,
      `Deposit: $${(booking.depositAmount / 100).toFixed(2)} paid`,
      `Balance: $${(booking.remainingAmount / 100).toFixed(2)} due on check-in day`,
      ``,
      `Booking code: ${code}`,
      `Enter this code in My stay to open the exact address, payment details, and host chat.`,
      `The door code is shown only after you pay the balance on check-in day. It is not in this email.`,
      ``,
      `Do not share this code.`,
    ].join("\n"),
    html: `<div style="font-family:Georgia,serif;color:#1c1712;background:#f4eee6;padding:32px">
      <h1 style="font-weight:500">Your stay is confirmed</h1>
      <p>${booking.listing.title}<br/>${booking.checkIn} → ${booking.checkOut}</p>
      <p style="letter-spacing:0.2em;font-size:22px;border:1px solid #c45c26;padding:12px 16px;display:inline-block">${code}</p>
      <p>Enter this code in <b>My stay</b>. The door code is not in this email.</p>
    </div>`,
    userId: booking.guestId ?? undefined,
    href: "/my-stay",
  });
  if (booking.guestId) {
    await notifyUser(booking.guestId, {
      title: "Your stay is confirmed",
      body: "Check your email for the booking code.",
      url: "/my-stay",
    });
  }
  const host = await prisma.user.findFirst({ where: { role: "HOST" } });
  if (host) {
    await notifyUser(host.id, {
      title: "New booking",
      body: `${booking.listing.title} · ${booking.checkIn}`,
      url: "/host/guests",
    });
    await sendMessage({
      channel: "email",
      to: host.email ?? env().hostEmail,
      subject: `New booking · ${booking.listing.title}`,
      body: `${booking.guestName} (${booking.guestEmail}) · ${booking.checkIn}–${booking.checkOut}`,
      userId: host.id,
      href: "/host/guests",
    });
  }
  void todayNY;
}

export async function getAvailability(slug: string) {
  const listing = await prisma.listing.findFirst({ where: { slug, published: true } });
  if (!listing) return { ok: false as const, ranges: [] as { checkIn: string; checkOut: string }[] };
  const ranges = await prisma.booking.findMany({
    where: { listingId: listing.id, status: { in: ["CONFIRMED", "AWAITING_REMAINING", "PAID_IN_FULL"] } },
    select: { checkIn: true, checkOut: true },
  });
  return { ok: true as const, ranges, maxGuests: listing.maxGuests };
}

export async function lookupPromo(code: string) {
  const promo = await prisma.promoCode.findUnique({ where: { code: code.trim().toUpperCase() } });
  if (!promo?.active) return { ok: false as const, error: "That code isn’t valid." };
  return { ok: true as const, percentOff: promo.percentOff, description: promo.description };
}

/** used by webhook */
export async function applyStripePayment(bookingId: string, kind: string) {
  const booking = await prisma.booking.findUnique({ where: { id: bookingId } });
  if (!booking) return;
  if (kind === "deposit" && !booking.depositPaidAt) {
    await finalizeDeposit(bookingId);
  }
  if (kind === "remaining" && !booking.remainingPaidAt) {
    await markRemainingPaid(bookingId);
  }
}
